import { AsyncPipe } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  inject,
} from '@angular/core';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import { CognitoAuthService } from '@techspert-io/auth';
import { EnrichedUser, UserService } from '@techspert-io/users';
import moment from 'moment';
import {
  EMPTY,
  Observable,
  combineLatest,
  finalize,
  map,
  shareReplay,
  switchMap,
  takeWhile,
  tap,
} from 'rxjs';
import { MaterialModule } from '../../../../shared/patterns/material-module';
import { OutreachEmailContent } from '../../models/outreach-email.forms';
import {
  CampaignEmailTemplateTypeMap,
  IOutreachEmail,
  IOutreachEmailRes,
  IOutreachEmailTemplate,
} from '../../models/outreach-email.models';
import { OutreachEmailTemplatesService } from '../../services/outreach-email-templates.service';
import { OutreachEmailService } from '../../services/outreach-email.service';
import {
  IOutreackEmailSummary,
  OutreachEmailConfirmationComponent,
} from '../outreach-email-confirmation/outreach-email-confirmation.component';
import { OutreachEmailContentFormComponent } from '../outreach-email-content-form/outreach-email-content-form.component';

const dateFormat = 'YYYY-MM-DD';
const timeFormat = 'HH:mm';

export interface IExpertEmailData {
  expertId: string;
  emails: { address: string; isPrimary: boolean }[];
  opporunitySegmentId: string;
  expertName?: string;
  honoraria?: {
    currency: string;
    amount: number;
  };
}

type FormGroupOutreachEmail = FormGroup<
  {
    receipientEmail: FormControl<string>;
    senderUserId: FormControl<string>;
  } & OutreachEmailContent
>;

@Component({
  selector: 'app-outreach-email',
  templateUrl: './outreach-email.component.html',
  styleUrls: ['./outreach-email.component.scss'],
  imports: [
    ReactiveFormsModule,
    MaterialModule,
    AsyncPipe,
    OutreachEmailContentFormComponent,
  ],
})
export class OutreachEmailComponent implements OnInit {
  @Input() emailData: IExpertEmailData;
  @Input() showCancel = false;
  @Output() emailSent = new EventEmitter<IOutreachEmailRes>();
  @Output() cancelled = new EventEmitter<void>();

  private readonly route = inject(ActivatedRoute);
  private readonly templatesService = inject(OutreachEmailTemplatesService);
  private readonly userService = inject(UserService);
  private readonly cognitoAuthService = inject(CognitoAuthService);
  private readonly outreachEmailService = inject(OutreachEmailService);
  readonly dialog = inject(MatDialog);

  isLoading = false;
  vmData$: Observable<{
    templates: IOutreachEmailTemplate[];
    users: EnrichedUser[];
  }> = EMPTY;

  outreachForm: FormGroupOutreachEmail = new FormGroup({
    receipientEmail: new FormControl<string>(null, Validators.required),
    senderUserId: new FormControl<string>(null, Validators.required),
    templateId: new FormControl<string>(null, Validators.required),
    templateType: new FormControl<CampaignEmailTemplateTypeMap>(
      null,
      Validators.required
    ),
    templateContent: new FormGroup({
      content: new FormControl<string>(null, Validators.required),
      subject: new FormControl<string>(null, Validators.required),
      preheader: new FormControl<string>(null),
    }),
    sendConfig: new FormGroup(
      {
        sendDate: new FormControl<string>(null, Validators.required),
        sendTime: new FormControl<string>(null, Validators.required),
      },
      this.validateEmailSendTimes()
    ),
    currency: new FormControl<string>('USD', Validators.required),
    honorarium: new FormControl<number>(300, Validators.required),
  });

  get expertEmails(): IExpertEmailData['emails'] {
    return (this.emailData?.emails || []).sort((a, b) =>
      a.address.localeCompare(b.address)
    );
  }

  private get formValues(): IOutreachEmail {
    const {
      senderUserId,
      receipientEmail,
      templateId,
      currency,
      honorarium,
      sendConfig,
      templateContent,
    } = this.outreachForm.controls;
    const { content, preheader, subject } = templateContent.controls;
    const { sendDate, sendTime } = sendConfig.controls;

    return {
      expertId: this.emailData.expertId,
      email: {
        senderUserId: senderUserId.value,
        recipientAddress: [receipientEmail.value],
        templateId: templateId.value,
        currency: currency.value,
        honorarium: honorarium.value,
        sendTime: moment(`${sendDate.value} ${sendTime.value}`).toISOString(),
        templateContent: {
          content: content.value,
          preheader: preheader.value,
          subject: subject.value,
        },
      },
    };
  }

  ngOnInit(): void {
    this.vmData$ = combineLatest([
      this.route.queryParamMap,
      this.templatesService.getTemplates(),
      this.userService.getInternalUsers(),
    ]).pipe(
      tap(([params]) => {
        const today = moment().add(1, 'h').startOf('hour');
        this.outreachForm.patchValue({
          receipientEmail:
            params.get('address') ||
            this.expertEmails.find((d) => d.isPrimary)?.address ||
            null,
          senderUserId: this.cognitoAuthService.loggedInUser.user_id,
          sendConfig: {
            sendDate: today.format(dateFormat),
            sendTime: today.format(timeFormat),
          },
          ...(this.emailData?.honoraria && {
            currency: this.emailData.honoraria.currency,
            honorarium: this.emailData.honoraria.amount,
          }),
        });
      }),
      map(([_, templates, users]) => ({
        users,
        templates,
      })),
      shareReplay(1)
    );
  }

  onSubmit(templates: IOutreachEmailTemplate[], users: EnrichedUser[]): void {
    this.outreachForm.markAllAsTouched();
    if (this.outreachForm.valid) {
      this.dialog
        .open<
          OutreachEmailConfirmationComponent,
          IOutreackEmailSummary,
          boolean
        >(OutreachEmailConfirmationComponent, {
          data: {
            ...this.formValues,
            senderEmail: users.find(
              (d) => d.user_id === this.formValues.email.senderUserId
            )?.email,
            templateName: templates.find(
              (t) => t.templateId === this.formValues.email.templateId
            )?.name,
          },
          minWidth: '780px',
          height: 'max-content',
        })
        .afterClosed()
        .pipe(
          takeWhile((res) => !!res),
          tap(() => (this.isLoading = true)),
          switchMap(() => this.outreachEmailService.send(this.formValues)),
          tap((res) => this.emailSent.emit(res)),
          finalize(() => (this.isLoading = false))
        )
        .subscribe();
    }
  }

  private validateEmailSendTimes(): ValidatorFn {
    return (ctrl: OutreachEmailContent['sendConfig']): ValidationErrors => {
      const { sendDate, sendTime } = ctrl.value;
      if (sendDate === null && sendTime === null) {
        return null;
      }

      const sendDateFull = moment(`${sendDate} ${sendTime}`);
      if (!sendDateFull.isValid()) {
        return {
          invalidDate: 'Invalid date',
        };
      }

      const isBefore = sendDateFull.isSameOrBefore(moment(), 'minute');
      const isAfter = sendDateFull.isSameOrAfter(
        moment().add(48, 'h'),
        'minute'
      );

      if (isBefore) {
        ctrl.controls.sendDate.setErrors({
          emailBeforeToday: `Is before today`,
        });
        ctrl.controls.sendTime.setErrors({
          emailBeforeToday: `Is before today`,
        });
      } else if (isAfter) {
        ctrl.controls.sendDate.setErrors({
          emailInFutureError: `Is in future`,
        });
        ctrl.controls.sendTime.setErrors({
          emailInFutureError: `Is in future`,
        });
      } else {
        ctrl.controls.sendDate.setErrors(null);
        ctrl.controls.sendTime.setErrors(null);
      }

      const errorsDict: Record<string, string> = {
        emailInFutureError: 'Emails must send within 48 hours',
        emailBeforeToday: 'Emails must be in the future',
      };

      return Object.keys(ctrl.controls.sendDate.errors || {}).reduce<
        Record<string, string>
      >(
        (acc, curr) => ({
          ...(acc || {}),
          [curr]: errorsDict[curr] || 'Unknown error',
        }),
        null
      );
    };
  }
}
